import {createContext, useContext} from "react";
import {useStructureConfigQuery, StructureConfigQuery} from "./queries.generated";
import {OutgoingCallBarring} from "@/api";
import {Features} from "@/api";
import {bootstrapClient} from "@/apollo-client/bootstrap";

type ServiceKey = keyof StructureConfigQuery["structureConfig"]["services"];

export class StructureConfig {
    private config: StructureConfigQuery["structureConfig"];

    constructor(data: StructureConfigQuery["structureConfig"]) {
        this.config = data;
    }

    isEnabledFeature = (name: keyof Features, isPreviewEnabled?: boolean): boolean => {
        if (!this.config) return false;

        const val = this.config.features[name];
        if (val === undefined) throw Error(`This feature (${name}) is not requested!`);
        if (val === "ENABLED") return true;
        if (val === "DISABLED") return false;
        return isPreviewEnabled === true;
    };

    isEnabledService = (name: ServiceKey): boolean => {
        if (!this.config) return false;

        if (name === "__typename") return false;
        return this.config?.services[name];
    };

    getOutgoingCallBarrings = (): OutgoingCallBarring[] => {
        return this.config?.outgoingCallBarrings;
    };

    get version(): string {
        return this.config?.version;
    }

    get sipProxy(): string {
        return this.config?.sipProxy;
    }

    get wssUris(): string[] {
        return this.config?.wssUris ?? [];
    }

    get chattoUrl(): string {
        return this.config?.chattoUrl;
    }

    get languages(): string[] {
        return this.config?.languages;
    }
}

const StructureConfigContext = createContext(undefined);

export function useStructureConfig(): StructureConfig {
    return useContext(StructureConfigContext);
}

export function StructureConfigProvider({children}: {children: React.ReactNode}) {
    const {data, loading, error} = useStructureConfigQuery({client: bootstrapClient});

    const structureConfig = loading || error ? new StructureConfig(null) : new StructureConfig(data.structureConfig);

    return <StructureConfigContext.Provider value={structureConfig}>{children}</StructureConfigContext.Provider>;
}
